<div
  class="bg-white w-full relative flex flex-col"
  [ngClass]="
    !isModal
      ? isOnboardingView
        ? 'max-h-[80vh] max-w-[1100px] rounded-xl m-auto'
        : 'max-h-[80vh] rounded-xl m-auto'
      : 'max-h-[90vh] rounded-lg'
  "
>
  @if (isModal) {
    <button class="absolute top-3 right-3 z-10" mat-icon-button (click)="closeModal()">
      <mat-icon>close</mat-icon>
    </button>
  }

  <div class="overflow-y-auto flex-1" [ngClass]="!isModal ? 'p-8' : ''">
    <div class="text-3xl font-bold" [ngClass]="!isModal ? 'mt-4' : 'mt-8'">
      {{ editMode ? ('BOTS.editBot' | translate) : ('BOTS.createNewBots' | translate) }}
    </div>

    <form [formGroup]="botSetupForm" class="py-4 pl-4 pr-9 bg-card overflow-hidden">
      @if (editMode) {
        <div
          class="flex items-center justify-between border-b border-gray-200 pb-4 mb-4 transition-opacity duration-200"
          [class.opacity-60]="!!botToEdit?.settings?.forceRedirect"
          role="group"
          [attr.aria-label]="'BOTS.botStatus' | translate"
        >
          <span class="text-2xl font-bold" id="bot-setup-status-heading">{{ 'BOTS.botStatus' | translate }}</span>
          <div class="flex items-center gap-3" role="presentation">
            <span class="text-xl" aria-live="polite">{{ botStatusLabelKey | translate }}</span>
            <mat-slide-toggle
              [hideIcon]="true"
              [checked]="!botToEdit?.settings?.forceRedirect"
              (change)="onBotStatusToggle($event)"
              (click)="$event.stopPropagation()"
              [attr.aria-label]="'BOTS.botActiveToggleAria' | translate"
            ></mat-slide-toggle>
          </div>
        </div>
      }

      <mat-accordion #accordion>
        @if (isChatToolsVisible) {
          <mat-expansion-panel [expanded]="step() === -1" (opened)="setStep(-1)" class="mb-3">
            <mat-expansion-panel-header>
              <mat-panel-title>{{ 'BOTS.chatTools' | translate }}</mat-panel-title>
            </mat-expansion-panel-header>
            <div class="flex flex-wrap gap-3 pt-2">
              <button
                mat-stroked-button
                type="button"
                class="chat-tool-action-btn"
                (click)="copyEmbeddableCodeToClipboard()"
              >
                <mat-icon>code</mat-icon>
                <span>{{ 'BUTTON_ACTIONS.viewEmbeddable' | translate }}</span>
              </button>
              <button mat-stroked-button type="button" class="chat-tool-action-btn" (click)="openChatWidgetFromSetup()">
                <mat-icon>play_arrow</mat-icon>
                <span>{{ 'BUTTON_ACTIONS.testChatBot' | translate }}</span>
              </button>
            </div>
          </mat-expansion-panel>
        }

        <!-- Account - SubAccount -->
        <mat-expansion-panel [expanded]="step() === 0" (opened)="setStep(0)">
          <mat-expansion-panel-header>
            <mat-panel-title>{{ 'partnerAndAccount' | translate }}</mat-panel-title>
            <mat-icon class="mr-2">group</mat-icon>
          </mat-expansion-panel-header>
          <div class="flex flex-col" [formGroup]="botSetupForm.get('step1')">
            <mat-form-field appearance="fill" class="w-full mb-4">
              <mat-label class="font-bold">{{ 'ACCOUNT.partners' | translate }}</mat-label>
              <mat-select
                formControlName="accounts"
                [placeholder]="'ACCOUNT.partners' | translate"
                panelClass="searchable-select-panel"
                (openedChange)="onSearchPanelOpenedChange(accountSearch, $event, accountSearchInput)"
                (selectionChange)="onSearchSelectionChange(accountSearch, $event.value)"
              >
                <mat-option [value]="accountSearch.sentinelValue" class="accounts-search-option">
                  <div
                    class="accounts-search-box"
                    (mousedown)="$event.preventDefault(); $event.stopPropagation()"
                    (click)="$event.stopPropagation()"
                    (keydown)="$event.stopPropagation()"
                    (keyup)="$event.stopPropagation()"
                  >
                    <mat-icon
                      class="accounts-search-box-icon"
                      [svgIcon]="'heroicons_solid:magnifying-glass'"
                      aria-hidden="true"
                    ></mat-icon>
                    <input
                      #accountSearchInput
                      type="text"
                      [formControl]="accountSearch.searchControl"
                      [placeholder]="'ACCOUNT.searchPartners' | translate"
                      class="accounts-search-box-input"
                      autocomplete="off"
                      (mousedown)="$event.stopPropagation()"
                      (click)="$event.stopPropagation()"
                      (keydown)="$event.stopPropagation()"
                      (keyup)="$event.stopPropagation()"
                    />
                  </div>
                </mat-option>
                @for (account of filteredAccounts$ | async; track account.accountUID ?? account._id) {
                  <mat-option [value]="account.accountUID ?? account._id">
                    {{ account.displayName }}
                  </mat-option>
                }
              </mat-select>
              <mat-icon class="icon-size-5" matPrefix [svgIcon]="'heroicons_solid:user'"></mat-icon>
              <mat-hint>{{ 'ACCOUNT.selectPartnersHint' | translate }}</mat-hint>
              @if (botSetupForm.get('step1.accounts')?.hasError('required') && botSetupForm.get('step1.accounts')?.touched) {
                <mat-error>
                  {{ 'ERRORS.partnersIsRequired' | translate }}
                </mat-error>
              }
            </mat-form-field>
            <mat-form-field appearance="fill" class="w-full mb-4">
              <mat-label class="font-bold">{{ 'ACCOUNT.accounts' | translate }}</mat-label>
              <mat-select
                formControlName="subAccount"
                [placeholder]="'ACCOUNT.accounts' | translate"
                panelClass="searchable-select-panel"
                (openedChange)="onSearchPanelOpenedChange(subAccountSearch, $event, subAccountSearchInput)"
                (selectionChange)="onSearchSelectionChange(subAccountSearch, $event.value)"
              >
                <mat-option [value]="subAccountSearch.sentinelValue" class="accounts-search-option">
                  <div
                    class="accounts-search-box"
                    (mousedown)="$event.preventDefault(); $event.stopPropagation()"
                    (click)="$event.stopPropagation()"
                    (keydown)="$event.stopPropagation()"
                    (keyup)="$event.stopPropagation()"
                  >
                    <mat-icon
                      class="accounts-search-box-icon"
                      [svgIcon]="'heroicons_solid:magnifying-glass'"
                      aria-hidden="true"
                    ></mat-icon>
                    <input
                      #subAccountSearchInput
                      type="text"
                      [formControl]="subAccountSearch.searchControl"
                      [placeholder]="'ACCOUNT.searchAccount' | translate"
                      class="accounts-search-box-input"
                      autocomplete="off"
                      (mousedown)="$event.stopPropagation()"
                      (click)="$event.stopPropagation()"
                      (keydown)="$event.stopPropagation()"
                      (keyup)="$event.stopPropagation()"
                    />
                  </div>
                </mat-option>
                @for (subAccount of filteredSubAccounts$ | async; track subAccount.subAccountUID ?? subAccount._id) {
                  <mat-option [value]="subAccount.subAccountUID">
                    {{ subAccount.displayName }}
                  </mat-option>
                }
              </mat-select>
              <mat-icon class="icon-size-5" matPrefix [svgIcon]="'heroicons_solid:user'"></mat-icon>
              <mat-hint>{{ 'ACCOUNT.selectAccountHint' | translate }}</mat-hint>
              @if (
                botSetupForm.get('step1.subAccount')?.hasError('required') && botSetupForm.get('step1.subAccount')?.touched
              ) {
                <mat-error>
                  {{ 'ERRORS.accountIsRequired' | translate }}
                </mat-error>
              }
            </mat-form-field>
          </div>
          <div class="flex mt-4 justify-end">
            <button
              class="px-8 responsive-btn flex-row-reverse"
              mat-flat-button
              color="primary"
              (click)="nextStep()"
              [disabled]="botSetupForm.get('step1')?.invalid"
            >
              <span class="btn-text">{{ 'BUTTON_ACTIONS.next' | translate }}</span>
              <mat-icon>arrow_forward</mat-icon>
            </button>
          </div>
        </mat-expansion-panel>

        <!-- Bot Basic Data -->
        <mat-expansion-panel [expanded]="step() === 1" (opened)="setStep(1)" [disabled]="!isStepValid(0)">
          <mat-expansion-panel-header>
            <mat-panel-title>{{ 'BOTS.botInformation' | translate }}</mat-panel-title>
            <mat-icon class="mr-2">smart_toy</mat-icon>
          </mat-expansion-panel-header>
          <div class="flex flex-col" [formGroup]="botSetupForm.get('step2')">
            <mat-form-field appearance="fill" class="w-full my-4">
              <mat-label class="font-bold">{{ 'BOTS.botName' | translate }}</mat-label>
              <input matInput formControlName="displayNameBot" />
              <mat-hint>{{ 'BOTS.botNameHint' | translate }}</mat-hint>
              @if (
                botSetupForm.get('step2.displayNameBot')?.hasError('required') &&
                botSetupForm.get('step2.displayNameBot')?.touched
              ) {
                <mat-error>
                  {{ 'ERRORS.botNameRequired' | translate }}
                </mat-error>
              }
              @if (
                botSetupForm.get('step2.displayNameBot')?.hasError('pattern') &&
                botSetupForm.get('step2.displayNameBot')?.touched
              ) {
                <mat-error>
                  {{ 'BOTS.onlyLettersAndNumbersAreAllowed' | translate }}
                </mat-error>
              }
            </mat-form-field>
            <mat-form-field appearance="fill" class="w-full my-4">
              <mat-label class="font-bold">{{ 'BOTS.botType' | translate }}</mat-label>
              <mat-select formControlName="type">
                @for (origin of origins; track $index) {
                  <mat-option [value]="origin.value">
                    {{ origin.label | uppercase }}
                  </mat-option>
                }
              </mat-select>
              <mat-hint>{{ 'BOTS.botTypeHint' | translate }}</mat-hint>
              @if (botSetupForm.get('step2.type')?.hasError('required') && botSetupForm.get('step2.type')?.touched) {
                <mat-error>
                  {{ 'ERRORS.botTypeRequired' | translate }}
                </mat-error>
              }
            </mat-form-field>
            <mat-form-field appearance="fill" class="w-full my-4">
              <mat-label class="font-bold">{{ 'BOTS.origin' | translate }}</mat-label>
              <input matInput formControlName="origin" />
              @if (botSetupForm.get('step2.type')?.value !== 'TEXT' && botSetupForm.get('step2.type')?.value !== 'SMS') {
                <mat-hint>{{ 'ERRORS.originHint' | translate }}</mat-hint>
              }
              @if (botSetupForm.get('step2.origin')?.hasError('required') && botSetupForm.get('step2.origin')?.touched) {
                <mat-error>
                  {{ 'ERRORS.originRequired' | translate }}
                </mat-error>
              }
              @if (botSetupForm.get('step2.origin')?.hasError('pattern') && botSetupForm.get('step2.origin')?.touched) {
                <mat-error>
                  {{ 'ERRORS.originInvalid' | translate }}
                </mat-error>
              }
            </mat-form-field>
          </div>
          <div class="flex mt-4 justify-end">
            <div class="flex">
              <button class="responsive-btn px-8 mr-2" mat-flat-button color="accent" (click)="prevStep()">
                <mat-icon>arrow_back</mat-icon>
                <span class="btn-text">{{ 'BUTTON_ACTIONS.back' | translate }}</span>
              </button>
              <button
                class="responsive-btn px-8 flex-row-reverse"
                mat-flat-button
                color="primary"
                [disabled]="botSetupForm.get('step2')?.invalid"
                (click)="nextStep()"
              >
                <span class="btn-text">{{ 'BUTTON_ACTIONS.next' | translate }}</span>
                <mat-icon>arrow_forward</mat-icon>
              </button>
            </div>
          </div>
        </mat-expansion-panel>

        <!-- Automatic Tagging Rules -->
        <mat-expansion-panel
          [expanded]="step() === 2"
          (opened)="onAutomaticTaggingRulesPanelOpened()"
          [disabled]="!isStepValid(1)"
        >
          <mat-expansion-panel-header>
            <mat-panel-title>Automatic Tagging Rules</mat-panel-title>
            <mat-icon class="mr-2">sell</mat-icon>
          </mat-expansion-panel-header>
          <div class="flex flex-col">
            <div class="text-2xl font-bold mt-2">Automatic Tagging Rules</div>
            <p class="text-secondary mt-2">Configure how tags are automatically applied to interactions from this bot.</p>

            <div class="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 my-6">
              <mat-icon class="text-amber-700">warning_amber</mat-icon>
              <span class="text-amber-800">Tagged interactions can be excluded from billing and usage reports.</span>
            </div>

            <div class="text-xl font-semibold">General Tags</div>
            <p class="text-secondary mt-2 mb-4">
              These tags will be applied to every interaction generated by this bot. Applies to all conversations handled by
              this bot.
            </p>

            <mat-form-field appearance="fill" class="w-full mb-4">
              <mat-select
                multiple
                [placeholder]="'Select tags...'"
                [value]="selectedGeneralTags"
                (selectionChange)="handleGeneralTagsChange($event.value)"
              >
                <mat-select-trigger>
                  @if (selectedGeneralTags.length > 0) {
                    {{ selectedGeneralTags.length }} tags selected
                  } @else {
                    Select tags...
                  }
                </mat-select-trigger>
                @for (tag of generalTagOptions; track tag) {
                  <mat-option [value]="tag">{{ tag }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            @if (selectedGeneralTags.length > 0) {
              <div class="flex flex-wrap gap-3 mb-6">
                @for (selectedTag of selectedGeneralTags; track selectedTag) {
                  <div class="flex items-center bg-gray-100 rounded-[14px] px-2">
                    <span>{{ selectedTag }}</span>
                    <button
                      type="button"
                      class="ml-2 leading-none"
                      mat-icon-button
                      (click)="removeGeneralTag(selectedTag)"
                      aria-label="Remove selected general tag"
                    >
                      <mat-icon class="icon-size-4">close</mat-icon>
                    </button>
                  </div>
                }
              </div>
            }

            <mat-divider class="mb-6"></mat-divider>

            <div class="text-xl font-semibold">Identifier-Based Tags</div>
            <p class="text-secondary mt-2 mb-4">
              Apply specific tags only when interactions involve certain identifiers (such as phone numbers or test
              accounts).
            </p>

            @if (identifierTagRules.length > 0) {
              <div class="flex flex-col gap-4 mb-4">
                @for (rule of identifierTagRules; track rule.id; let i = $index) {
                  <div class="rounded-lg border border-gray-200 p-4 relative">
                    <button
                      type="button"
                      class="absolute top-3 right-3 delete-rule-btn"
                      (click)="removeIdentifierRule(i)"
                      aria-label="Remove tagging rule"
                    >
                      <mat-icon>delete_outline</mat-icon>
                    </button>

                    <mat-label class="font-bold">Tag to apply</mat-label>
                    <mat-form-field appearance="fill" class="w-full my-2">
                      <mat-select [value]="rule.tag" (selectionChange)="patchIdentifierRule(i, { tag: $event.value })">
                        @for (tag of getIdentifierTagOptions(i); track tag) {
                          <mat-option [value]="tag">{{ tag }}</mat-option>
                        }
                      </mat-select>
                    </mat-form-field>

                    <mat-label class="font-bold">Identifiers</mat-label>
                    <div class="flex items-stretch gap-2 mt-2 identifier-input-row">
                      <mat-form-field appearance="fill" subscriptSizing="dynamic" class="w-full identifier-input-field">
                        <input
                          matInput
                          [ngModel]="rule.currentIdentifier"
                          (ngModelChange)="patchIdentifierRule(i, { currentIdentifier: $event })"
                          placeholder="Example: +15551234567"
                          [ngModelOptions]="{ standalone: true }"
                          (keydown.enter)="$event.preventDefault(); addIdentifierToRule(i)"
                        />
                      </mat-form-field>
                      <button
                        type="button"
                        class="px-5 responsive-btn identifier-item-add-btn"
                        mat-flat-button
                        color="primary"
                        (click)="addIdentifierToRule(i)"
                        [disabled]="isIdentifierAddDisabled(rule)"
                      >
                        <mat-icon>add</mat-icon>
                        Add
                      </button>
                    </div>
                    @if (rule.currentIdentifier.trim().length > 0 && !isPhoneIdentifier(rule.currentIdentifier)) {
                      <span class="text-sm text-red-500 block my-1">
                        {{ 'ERRORS.invalidPhoneIdentifierWithCountryCode' | translate }}
                      </span>
                    }

                    @if (rule.identifiers.length > 0) {
                      <div class="flex flex-wrap gap-3">
                        @for (identifier of rule.identifiers; track identifier) {
                          <div class="flex items-center bg-gray-100 rounded-[14px] px-2 mt-2">
                            <span>{{ identifier }}</span>
                            <button
                              type="button"
                              class="ml-2 leading-none"
                              mat-icon-button
                              (click)="removeIdentifierFromRule(i, identifier)"
                              aria-label="Remove identifier"
                            >
                              <mat-icon class="icon-size-4">close</mat-icon>
                            </button>
                          </div>
                        }
                      </div>
                    }
                  </div>
                }
              </div>
            }

            <div class="mb-4">
              <button
                type="button"
                class="px-6 py-2 border border-dashed rounded-md border-gray-300 text-secondary flex items-center justify-center gap-2 identifier-add-rule-btn"
                (click)="addIdentifierRule()"
                [disabled]="!canAddIdentifierRule"
              >
                <mat-icon>add</mat-icon>
                Add another tagging rule
              </button>
            </div>
          </div>
          <div class="flex mt-4 justify-end">
            <div class="flex">
              <button class="px-8 mr-2 responsive-btn" mat-flat-button color="accent" (click)="prevStep()">
                <mat-icon>arrow_back</mat-icon>
                <span class="btn-text">{{ 'BUTTON_ACTIONS.back' | translate }}</span>
              </button>
              <button class="px-8 responsive-btn flex-row-reverse" mat-flat-button color="primary" (click)="nextStep()">
                <span class="btn-text">{{ 'BUTTON_ACTIONS.next' | translate }}</span>
                <mat-icon>arrow_forward</mat-icon>
              </button>
            </div>
          </div>
        </mat-expansion-panel>

        <!-- Bot Configuration -->
        <mat-expansion-panel [expanded]="step() === 3" (opened)="setStep(3)" [disabled]="!isStepValid(1)">
          <mat-expansion-panel-header>
            <mat-panel-title>{{ 'BOTS.botConfiguration' | translate }}</mat-panel-title>
            <mat-icon class="mr-2">language</mat-icon>
          </mat-expansion-panel-header>
          <div class="flex flex-col" [formGroup]="botSetupForm.get('step3')">
            <mat-form-field appearance="fill" class="w-full my-4">
              <mat-label class="font-bold">{{ 'BOTS.botDefaultLanguage' | translate }}</mat-label>
              <mat-select formControlName="defaultLanguage" (selectionChange)="onDefaultLanguageChange()">
                @for (lang of visibleLanguageOptions; track $index) {
                  <mat-option [value]="lang.value">{{ lang.label }}</mat-option>
                }
              </mat-select>
              <mat-hint>{{ 'BOTS.selectBotLanguageHint' | translate }}</mat-hint>
              @if (
                botSetupForm.get('step3.defaultLanguage')?.hasError('required') &&
                botSetupForm.get('step3.defaultLanguage')?.touched
              ) {
                <mat-error>
                  {{ 'ERRORS.defaultLanguageRequired' | translate }}
                </mat-error>
              }
            </mat-form-field>
            <div class="flex items-center">
              <div class="w-full mb-4">
                <mat-form-field appearance="fill" class="w-full">
                  <mat-label class="font-bold">{{ 'BOTS.welcomeMessage' | translate }}</mat-label>
                  <textarea matInput formControlName="welcomeMessage"></textarea>
                  <mat-hint>{{ 'BOTS.welcomeMessageHint' | translate }}</mat-hint>
                  @if (
                    botSetupForm.get('step3.welcomeMessage')?.hasError('required') &&
                    botSetupForm.get('step3.welcomeMessage')?.touched
                  ) {
                    <mat-error>
                      {{ 'ERRORS.welcomeMessageRequired' | translate }}
                    </mat-error>
                  }
                </mat-form-field>
                <div class="welcome-default-meta-row">
                  <button
                    type="button"
                    class="welcome-default-reset-btn"
                    (click)="resetWelcomeMessageToDefault('welcomeMessage')"
                  >
                    {{ 'BOTS.resetToDefault' | translate }}
                  </button>
                </div>
                @if (welcomeGenerationError) {
                  <div class="welcome-generation-error">
                    <mat-icon class="welcome-generation-error-icon">error_outline</mat-icon>
                    <span>{{ 'BOTS.welcomeGenerationError' | translate }}</span>
                    <button
                      type="button"
                      mat-stroked-button
                      color="warn"
                      [attr.aria-label]="'BUTTON_ACTIONS.retry' | translate"
                      (click)="retryWelcomeGeneration()"
                    >
                      <mat-icon>refresh</mat-icon>
                      {{ 'BUTTON_ACTIONS.retry' | translate }}
                    </button>
                  </div>
                }
              </div>
              @if (botSetupForm.get('step2.type')?.value === 'VOICE') {
                <button class="ml-4 mr-2" mat-icon-button (click)="toggleAudio('welcomeMessage')">
                  <mat-icon
                    [ngClass]="isPlayingOpenHours ? 'material-symbols-outlined animate-eq' : 'material-symbols-outlined'"
                    >{{ isPlayingOpenHours ? 'graphic_eq' : 'play_arrow' }}</mat-icon
                  >
                </button>
                <audio #openHoursAudio id="openHoursAudio"></audio>
                <mat-icon matTooltip="{{ 'voiceBots' | translate }}" class="material-symbols-outlined"
                  >{{ 'info' }}
                </mat-icon>
              }
            </div>
            <div class="flex items-center">
              <div class="w-full mb-4">
                <mat-form-field appearance="fill" class="w-full">
                  <mat-label class="font-bold">{{ 'BOTS.welcomeMessageOffHours' | translate }}</mat-label>
                  <textarea matInput formControlName="welcomeMessageoffHours"></textarea>
                  <mat-hint>{{ 'BOTS.welcomeMessageOffHoursHint' | translate }}</mat-hint>
                </mat-form-field>
                <div class="welcome-default-meta-row">
                  <button
                    type="button"
                    class="welcome-default-reset-btn"
                    (click)="resetWelcomeMessageToDefault('welcomeMessageoffHours')"
                  >
                    {{ 'BOTS.resetToDefault' | translate }}
                  </button>
                </div>
                @if (welcomeGenerationError) {
                  <div class="welcome-generation-error">
                    <mat-icon class="welcome-generation-error-icon">error_outline</mat-icon>
                    <span>{{ 'BOTS.welcomeGenerationError' | translate }}</span>
                    <button
                      type="button"
                      mat-stroked-button
                      color="warn"
                      [attr.aria-label]="'BUTTON_ACTIONS.retry' | translate"
                      (click)="retryWelcomeGeneration()"
                    >
                      <mat-icon>refresh</mat-icon>
                      {{ 'BUTTON_ACTIONS.retry' | translate }}
                    </button>
                  </div>
                }
              </div>
              @if (botSetupForm.get('step2.type')?.value === 'VOICE') {
                <button class="ml-4 mr-2" mat-icon-button (click)="toggleAudio('welcomeMessageoffHours')">
                  <mat-icon
                    [ngClass]="isPlayingOffHours ? 'material-symbols-outlined animate-eq' : 'material-symbols-outlined'"
                    >{{ isPlayingOffHours ? 'graphic_eq' : 'play_arrow' }}</mat-icon
                  >
                </button>
                <audio #offHoursAudio id="offHoursAudio"></audio>
                <mat-icon matTooltip="{{ 'voiceBots' | translate }}" class="material-symbols-outlined"
                  >{{ 'info' }}
                </mat-icon>
              }
            </div>
            <!-- Voice flavor + tone/friendliness/professionalism: AIVA1 only. An AIVA2
                 bot picks its voice in the Voice Audition panel instead, and the
                 engine and voicebot no longer apply the flavor to it (it used to
                 replace the auditioned voice after the greeting was rendered).
                 Hidden too when the pipeline never speaks in the bot's own voice
                 (see botVoiceIsUsed). -->
            @if (botSetupForm.get('step2.type')?.value === 'VOICE' && !isAiva2Bot && botVoiceIsUsed) {
              <div class="mt-6 border-t border-border pt-4">
                <p class="font-semibold text-sm mb-1 m-0">{{ 'BOTS.voiceFlavorTitle' | translate }}</p>
                <p class="text-sm text-muted-foreground mb-4 m-0">
                  {{ 'BOTS.voiceFlavorDescription' | translate }}
                </p>
                <mat-form-field appearance="fill" class="w-full mb-4">
                  <mat-label class="font-bold">{{ 'BOTS.voiceFlavor' | translate }}</mat-label>
                  <mat-select formControlName="voiceFlavor" (selectionChange)="onVoiceFlavorChange()">
                    @for (preset of voiceFlavorPresets; track preset.id) {
                      <mat-option [value]="preset.id">
                        {{ preset.displayName }}
                        ({{ getVoiceTypeLabel(preset.voiceType) | translate }})
                      </mat-option>
                    }
                  </mat-select>
                  <mat-hint>
                    {{ getSelectedVoiceFlavorPreset()?.description }}
                    @if (getSelectedVoiceFlavorPreset()?.voiceType) {
                      · {{ getVoiceTypeLabel(getSelectedVoiceFlavorPreset()?.voiceType) | translate }}
                    }
                  </mat-hint>
                </mat-form-field>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <mat-form-field appearance="fill" class="w-full">
                    <mat-label>{{ 'BOTS.voiceTone' | translate }}</mat-label>
                    <mat-select formControlName="voiceTone">
                      @for (level of voiceAttributeLevels; track level) {
                        <mat-option [value]="level">{{ 'BOTS.voiceAttribute.' + level | translate }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                  <mat-form-field appearance="fill" class="w-full">
                    <mat-label>{{ 'BOTS.voiceFriendliness' | translate }}</mat-label>
                    <mat-select formControlName="voiceFriendliness">
                      @for (level of voiceAttributeLevels; track level) {
                        <mat-option [value]="level">{{ 'BOTS.voiceAttribute.' + level | translate }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                  <mat-form-field appearance="fill" class="w-full">
                    <mat-label>{{ 'BOTS.voiceProfessionalism' | translate }}</mat-label>
                    <mat-select formControlName="voiceProfessionalism">
                      @for (level of voiceAttributeLevels; track level) {
                        <mat-option [value]="level">{{ 'BOTS.voiceAttribute.' + level | translate }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                </div>
                <button type="button" mat-stroked-button color="primary" (click)="previewSelectedVoiceFlavor()">
                  <mat-icon
                    [ngClass]="
                      isPlayingVoiceFlavorPreview ? 'material-symbols-outlined animate-eq' : 'material-symbols-outlined'
                    "
                  >
                    {{ isPlayingVoiceFlavorPreview ? 'graphic_eq' : 'play_arrow' }}
                  </mat-icon>
                  {{ 'BOTS.previewVoiceFlavor' | translate }}
                </button>
                <p class="text-xs text-muted-foreground mt-4 mb-0">
                  {{ 'BOTS.customVoicePersonaNote' | translate }}
                </p>
              </div>
            }

            <!-- AIVA-1767: visible inside Bot Configuration -->
            <div class="flex items-center justify-between py-4 mt-4 border-t border-gray-200 dark:border-gray-700">
              <div class="flex flex-col pr-4">
                <span class="font-bold">{{ 'BOTS.enableRepairCostEstimate' | translate }}</span>
                <span class="text-secondary text-sm">
                  {{ 'BOTS.enableRepairCostEstimateDescription' | translate }}
                </span>
              </div>
              <mat-slide-toggle
                formControlName="enableRepairCostEstimate"
                [disabled]="!editMode || !botToEdit?._id || repairCostEstimateUpdating"
                [attr.aria-label]="'BOTS.enableRepairCostEstimate' | translate"
                (change)="onRepairCostEstimateToggle($event)"
              ></mat-slide-toggle>
            </div>

            <!-- AIVA-1768: rides on Repair Cost Estimate — no price range to finance without it -->
            <div class="flex items-center justify-between py-4 border-t border-gray-200 dark:border-gray-700">
              <div class="flex flex-col pr-4">
                <span class="font-bold">{{ 'BOTS.enableFinancingEstimate' | translate }}</span>
                <span class="text-secondary text-sm">
                  {{
                    (botSetupForm.get('step3.enableRepairCostEstimate')?.value
                      ? 'BOTS.enableFinancingEstimateDescription'
                      : 'BOTS.enableFinancingEstimateRequiresRepairCostEstimate'
                    ) | translate
                  }}
                </span>
              </div>
              <mat-slide-toggle
                formControlName="enableFinancingEstimate"
                [disabled]="
                  !editMode ||
                  !botToEdit?._id ||
                  financingEstimateUpdating ||
                  (!botSetupForm.get('step3.enableRepairCostEstimate')?.value &&
                    !botSetupForm.get('step3.enableFinancingEstimate')?.value)
                "
                [attr.aria-label]="'BOTS.enableFinancingEstimate' | translate"
                (change)="onFinancingEstimateToggle($event)"
              ></mat-slide-toggle>
            </div>
          </div>
          <div class="flex mt-4 justify-end">
            <div class="flex">
              <button class="px-8 mr-2 responsive-btn" mat-flat-button color="accent" (click)="prevStep()">
                <mat-icon>arrow_back</mat-icon>
                <span class="btn-text">{{ 'BUTTON_ACTIONS.back' | translate }}</span>
              </button>
              <button
                class="px-8 responsive-btn flex-row-reverse"
                mat-flat-button
                color="primary"
                (click)="nextStep()"
                [disabled]="botSetupForm.get('step3')?.invalid"
              >
                <span class="btn-text">{{ 'BUTTON_ACTIONS.next' | translate }}</span>
                <mat-icon>arrow_forward</mat-icon>
              </button>
            </div>
          </div>
        </mat-expansion-panel>

        <!-- Voice audition (AIVA-2150, voice bots only, AIVA2-exclusive per Section 9).
             Hidden when the pipeline never speaks in the bot's own voice (REALTIME,
             FLUX — see botVoiceIsUsed). -->
        @if (isAiva2Bot && botSetupForm.get('step2.type')?.value === 'VOICE' && botVoiceIsUsed) {
          <mat-expansion-panel [expanded]="step() === 13" (opened)="setStep(13)">
            <mat-expansion-panel-header>
              <mat-panel-title>{{ 'BOTS.voiceCatalogTitle' | translate }}</mat-panel-title>
              <mat-icon class="mr-2">record_voice_over</mat-icon>
            </mat-expansion-panel-header>
            <div class="flex flex-col">
              <p class="text-secondary mt-2 mb-4">{{ 'BOTS.voiceCatalogDescription' | translate }}</p>
              <app-voice-catalog [botId]="botToEdit?._id" [languages]="auditionLanguages"></app-voice-catalog>
            </div>
          </mat-expansion-panel>
        }

        <!-- AIVA-1767: also listed as its own Edit Bot section -->
        <mat-expansion-panel [expanded]="step() === 11" (opened)="setStep(11)" [disabled]="!editMode || !botToEdit?._id">
          <mat-expansion-panel-header>
            <mat-panel-title>{{ 'BOTS.repairCostEstimate' | translate }}</mat-panel-title>
            <mat-icon class="mr-2">attach_money</mat-icon>
          </mat-expansion-panel-header>
          <div class="flex flex-col" [formGroup]="botSetupForm.get('step3')">
            <div class="flex items-center justify-between py-2">
              <div class="flex flex-col pr-4">
                <span class="font-bold">{{ 'BOTS.enableRepairCostEstimate' | translate }}</span>
                <span class="text-secondary text-sm">
                  {{ 'BOTS.enableRepairCostEstimateDescription' | translate }}
                </span>
              </div>
              <mat-slide-toggle
                formControlName="enableRepairCostEstimate"
                [disabled]="!editMode || !botToEdit?._id || repairCostEstimateUpdating"
                [attr.aria-label]="'BOTS.enableRepairCostEstimate' | translate"
                (change)="onRepairCostEstimateToggle($event)"
              ></mat-slide-toggle>
            </div>

            <!-- AIVA-1768 -->
            <div class="flex items-center justify-between py-2 border-t border-gray-200 dark:border-gray-700">
              <div class="flex flex-col pr-4">
                <span class="font-bold">{{ 'BOTS.enableFinancingEstimate' | translate }}</span>
                <span class="text-secondary text-sm">
                  {{
                    (botSetupForm.get('step3.enableRepairCostEstimate')?.value
                      ? 'BOTS.enableFinancingEstimateDescription'
                      : 'BOTS.enableFinancingEstimateRequiresRepairCostEstimate'
                    ) | translate
                  }}
                </span>
              </div>
              <mat-slide-toggle
                formControlName="enableFinancingEstimate"
                [disabled]="
                  !editMode ||
                  !botToEdit?._id ||
                  financingEstimateUpdating ||
                  (!botSetupForm.get('step3.enableRepairCostEstimate')?.value &&
                    !botSetupForm.get('step3.enableFinancingEstimate')?.value)
                "
                [attr.aria-label]="'BOTS.enableFinancingEstimate' | translate"
                (change)="onFinancingEstimateToggle($event)"
              ></mat-slide-toggle>
            </div>
          </div>
          <div class="flex mt-4 justify-end">
            <div class="flex">
              <button class="px-8 mr-2 responsive-btn" mat-flat-button color="accent" (click)="setStep(3)">
                <mat-icon>arrow_back</mat-icon>
                <span class="btn-text">{{ 'BUTTON_ACTIONS.back' | translate }}</span>
              </button>
              <button class="px-8 responsive-btn flex-row-reverse" mat-flat-button color="primary" (click)="setStep(4)">
                <span class="btn-text">{{ 'BUTTON_ACTIONS.next' | translate }}</span>
                <mat-icon>arrow_forward</mat-icon>
              </button>
            </div>
          </div>
        </mat-expansion-panel>

        <!--  Multilanguage Configuration -->
        <mat-expansion-panel [expanded]="step() === 4" (opened)="setStep(4)" [disabled]="!isStepValid(2)">
          <mat-expansion-panel-header>
            <mat-panel-title>{{ 'Multilanguage Configuration' }}</mat-panel-title>
            <mat-icon class="mr-2">translate</mat-icon>
          </mat-expansion-panel-header>
          <div class="flex flex-col" [formGroup]="botSetupForm.get('step4')">
            <mat-radio-group class="flex flex-col mb-4" formControlName="enableMultilanguage">
              <mat-label class="font-bold">{{ 'BOTS.enableMultilanguage' | translate }}*</mat-label>
              <mat-radio-button [value]="true">{{ 'BUTTON_ACTIONS.true' | translate | uppercase }}</mat-radio-button>
              <mat-radio-button [value]="false">{{ 'BUTTON_ACTIONS.false' | translate | uppercase }}</mat-radio-button>
              <mat-hint class="mt-6">{{ 'BOTS.enableMultilanguageHint' | translate }}</mat-hint>
            </mat-radio-group>
          </div>
          <div class="flex mt-4 justify-end">
            <div class="flex">
              <button class="px-8 mr-2 responsive-btn" mat-flat-button color="accent" (click)="prevStep()">
                <mat-icon>arrow_back</mat-icon>
                <span class="btn-text">{{ 'BUTTON_ACTIONS.back' | translate }}</span>
              </button>
              <button
                class="px-8 responsive-btn flex-row-reverse"
                mat-flat-button
                color="primary"
                (click)="nextStep()"
                [disabled]="botSetupForm.get('step4')?.invalid"
              >
                <span class="btn-text">{{ 'BUTTON_ACTIONS.next' | translate }}</span>
                <mat-icon>arrow_forward</mat-icon>
              </button>
            </div>
          </div>
        </mat-expansion-panel>

        <!-- Selection of Allowed Languages -->
        @if (showLanguageStep) {
          <mat-expansion-panel
            [expanded]="step() === 5 && showLanguageStep"
            (opened)="setStep(5)"
            [disabled]="!isStepValid(3)"
          >
            <mat-expansion-panel-header>
              <mat-panel-title>{{ 'BOTS.languageSelection' | translate }}</mat-panel-title>
              <mat-icon class="mr-2">language</mat-icon>
            </mat-expansion-panel-header>
            <div class="flex flex-col" [formGroup]="botSetupForm.get('step5.allowedLanguages')">
              <mat-label class="font-bold">{{ 'BOTS.allowedLanguages' | translate }}*</mat-label>
              @for (lang of visibleLanguageOptions; track $index) {
                <!-- The default language is always ticked and can't be unticked
                     (lockDefaultLanguageInAllowedList); not [disabled], which
                     would drop it from the saved list. -->
                <mat-checkbox
                  [formControlName]="lang.controlName"
                  [value]="lang.value"
                  [class.pointer-events-none]="isLockedDefaultLanguage(lang.value)"
                  [attr.aria-readonly]="isLockedDefaultLanguage(lang.value) || null"
                >
                  {{ lang.label }}
                  @if (isLockedDefaultLanguage(lang.value)) {
                    <span class="text-secondary">({{ 'BOTS.defaultLanguageAlwaysIncluded' | translate }})</span>
                  }
                </mat-checkbox>
              }
              <mat-hint class="mt-4">{{ 'BOTS.allowedLanguagesHint' | translate }}</mat-hint>
              @if (
                botSetupForm.get('step5.allowedLanguages')?.hasError('atLeastOneLanguage') &&
                botSetupForm.get('step5.allowedLanguages')?.touched
              ) {
                <mat-error>
                  {{ 'ERRORS.atLeastOneLanguageRequired' | translate }}
                </mat-error>
              }
            </div>
            <div class="flex mt-4 justify-end">
              <div class="flex">
                <button class="px-8 mr-2 responsive-btn" mat-flat-button color="accent" (click)="prevStep()">
                  <mat-icon>arrow_back</mat-icon>
                  <span class="btn-text">{{ 'BUTTON_ACTIONS.back' | translate }}</span>
                </button>
                <button
                  class="px-8 responsive-btn flex-row-reverse"
                  mat-flat-button
                  color="primary"
                  (click)="nextStep()"
                  [disabled]="botSetupForm.get('step5')?.invalid"
                >
                  <span class="btn-text">{{ 'BUTTON_ACTIONS.next' | translate }}</span>
                  <mat-icon>arrow_forward</mat-icon>
                </button>
              </div>
            </div>
          </mat-expansion-panel>
        }

        <!-- Transfer Number -->
        <mat-expansion-panel [expanded]="step() === 6" (opened)="setStep(6)" [disabled]="!isStepValid(3)">
          <mat-expansion-panel-header>
            <mat-panel-title>{{ 'BOTS.transferNumber' | translate }}</mat-panel-title>
            <mat-icon class="mr-2">phone</mat-icon>
          </mat-expansion-panel-header>
          <div class="flex flex-col" [formGroup]="botSetupForm.get('step6')">
            <mat-form-field appearance="fill" class="w-full my-4">
              <mat-label class="font-bold">{{ 'BOTS.transferPhoneNumber' | translate }}</mat-label>
              <input matInput type="text" formControlName="callForwardingPhoneNo" />
              <mat-hint>{{ 'ERRORS.transferNumberHint' | translate }}</mat-hint>
              @if (
                botSetupForm.get('step6.callForwardingPhoneNo')?.hasError('required') &&
                botSetupForm.get('step6.callForwardingPhoneNo')?.touched
              ) {
                <mat-error>
                  {{ 'ERRORS.phoneRequired' | translate }}
                </mat-error>
              }
              @if (
                botSetupForm.get('step6.callForwardingPhoneNo')?.hasError('pattern') &&
                botSetupForm.get('step6.callForwardingPhoneNo')?.touched
              ) {
                <mat-error>
                  {{ 'ERRORS.invalidPhone' | translate }}
                </mat-error>
              }
            </mat-form-field>
            <mat-form-field appearance="fill" class="w-full my-4">
              <mat-label class="font-bold">{{ 'BOTS.transferPhoneExtension' | translate }}</mat-label>
              <input matInput type="text" formControlName="callForwardingExtension" />
              <mat-hint>{{ 'BOTS.transferPhoneExtensionHint' | translate }}</mat-hint>
            </mat-form-field>
            @if (botSetupForm.get('step2.type')?.value === 'VOICE') {
              <mat-checkbox formControlName="enableDtmf" class="mb-4">
                {{ 'BOTS.enableDtmf' | translate }}
              </mat-checkbox>
              @if (botSetupForm.get('step6.enableDtmf')?.value) {
                <div class="rounded-lg border border-border p-4 mb-4">
                  <p class="font-semibold mb-3">{{ 'BOTS.dtmfMenuTitle' | translate }}</p>
                  <mat-form-field appearance="fill" class="w-full mb-3">
                    <mat-label>{{ 'BOTS.dtmfMenuPrompt' | translate }}</mat-label>
                    <input matInput type="text" formControlName="dtmfMenuPrompt" />
                  </mat-form-field>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <mat-form-field appearance="fill">
                      <mat-label>{{ 'BOTS.dtmfMaxRetries' | translate }}</mat-label>
                      <input matInput type="number" formControlName="dtmfMaxRetries" />
                    </mat-form-field>
                    <mat-form-field appearance="fill">
                      <mat-label>{{ 'BOTS.dtmfInputTimeoutMs' | translate }}</mat-label>
                      <input matInput type="number" formControlName="dtmfInputTimeoutMs" />
                    </mat-form-field>
                  </div>
                  <mat-form-field appearance="fill" class="w-full mb-3">
                    <mat-label>{{ 'BOTS.dtmfFallbackPrompt' | translate }}</mat-label>
                    <input matInput type="text" formControlName="dtmfFallbackPrompt" />
                  </mat-form-field>
                  <div formArrayName="dtmfMenuOptions" class="flex flex-col gap-3">
                    @for (option of dtmfMenuOptions.controls; track $index) {
                      <div class="grid grid-cols-1 md:grid-cols-6 gap-3 items-start" [formGroupName]="$index">
                        <mat-form-field appearance="fill">
                          <mat-label>{{ 'BOTS.dtmfDigit' | translate }}</mat-label>
                          <input matInput formControlName="digit" />
                        </mat-form-field>
                        <mat-form-field appearance="fill">
                          <mat-label>{{ 'BOTS.dtmfLabel' | translate }}</mat-label>
                          <input matInput formControlName="label" />
                        </mat-form-field>
                        <mat-form-field appearance="fill">
                          <mat-label>{{ 'BOTS.dtmfActionType' | translate }}</mat-label>
                          <mat-select formControlName="actionType">
                            <mat-option value="TRANSFER">{{ 'BOTS.dtmfActionTransfer' | translate }}</mat-option>
                            <mat-option value="REPEAT_MENU">{{ 'BOTS.dtmfActionRepeatMenu' | translate }}</mat-option>
                            <mat-option value="FLOW_BRANCH">{{ 'BOTS.dtmfActionFlowBranch' | translate }}</mat-option>
                          </mat-select>
                        </mat-form-field>
                        @if (option.get('actionType')?.value === 'FLOW_BRANCH') {
                          <mat-form-field appearance="fill">
                            <mat-label>{{ 'BOTS.dtmfFlowBranchId' | translate }}</mat-label>
                            <input matInput formControlName="flowBranchId" />
                          </mat-form-field>
                        }
                        @if (option.get('actionType')?.value === 'TRANSFER') {
                          <mat-form-field appearance="fill">
                            <mat-label>{{ 'BOTS.transferPhoneNumber' | translate }}</mat-label>
                            <input matInput formControlName="phoneNumber" />
                          </mat-form-field>
                          <mat-form-field appearance="fill">
                            <mat-label>{{ 'BOTS.transferPhoneExtension' | translate }}</mat-label>
                            <input matInput formControlName="extension" />
                          </mat-form-field>
                        }
                        <div class="flex items-center gap-2 pt-2">
                          <button mat-stroked-button type="button" color="warn" (click)="removeDtmfMenuOption($index)">
                            {{ 'BOTS.removeDtmfMenuOption' | translate }}
                          </button>
                        </div>
                      </div>
                    }
                  </div>
                  <button mat-stroked-button type="button" class="mt-3" (click)="addDtmfMenuOption()">
                    {{ 'BOTS.addDtmfMenuOption' | translate }}
                  </button>
                </div>
              }
            }
            <mat-form-field appearance="fill" class="w-full my-4">
              <mat-label class="font-bold">{{ 'BOTS.afterHoursForwardingPhrase' | translate }}</mat-label>
              <input matInput type="text" formControlName="afterHoursForwardingPhrase" />
              <mat-hint>{{ 'BOTS.afterHoursForwardingPhraseHint' | translate }}</mat-hint>
            </mat-form-field>
            @if (getTransferSubAccount()) {
              <div
                class="rounded-lg border border-gray-200 px-4 py-3 text-sm text-gray-700 dark:border-white/10 dark:text-gray-200"
              >
                <p class="font-semibold m-0">{{ 'BUSINESS_HOURS.previewTitle' | translate }}</p>
                <p class="mt-2 mb-0">
                  {{ getTransferBusinessHoursSummary() || ('BUSINESS_HOURS.notConfigured' | translate) }}
                </p>
                @if (getTransferBusinessHoursTimezone()) {
                  <p class="mt-2 mb-0">
                    {{ 'BUSINESS_HOURS.timezone' | translate }}: {{ getTransferBusinessHoursTimezone() }}
                  </p>
                }
                @if (getTransferBusinessHoursEditorQueryParams(); as queryParams) {
                  <a
                    class="inline-block mt-3 text-primary"
                    routerLink="/sub-accounts/sub-accounts-list"
                    [queryParams]="queryParams"
                  >
                    {{ 'BOTS.editDealershipHours' | translate }}
                  </a>
                }
              </div>
            }
            <div class="flex items-center gap-3 my-2">
              <mat-slide-toggle formControlName="allowTransferWithoutCustomer" color="primary">
                <span class="text-sm">{{ 'BOTS.allowTransferWithoutCustomer' | translate }}</span>
              </mat-slide-toggle>
            </div>
            @if (botSetupForm.get('step2.type')?.value === 'VOICE') {
              <div class="mt-6 border-t border-gray-200 pt-4 dark:border-white/10">
                <p class="font-semibold text-sm mb-3 m-0">{{ 'BOTS.enableVoicemail' | translate }}</p>
                <div class="flex items-center gap-3 my-2">
                  <mat-slide-toggle formControlName="enableVoicemail" color="primary">
                    <span class="text-sm">{{ 'BOTS.enableVoicemail' | translate }}</span>
                  </mat-slide-toggle>
                </div>
                <mat-form-field appearance="fill" class="w-full my-4">
                  <mat-label class="font-bold">{{ 'BOTS.voicemailConsentMessage' | translate }}</mat-label>
                  <textarea matInput rows="2" formControlName="voicemailConsentMessage"></textarea>
                  <mat-hint>{{ 'BOTS.voicemailConsentMessageHint' | translate }}</mat-hint>
                </mat-form-field>
                <mat-form-field appearance="fill" class="w-full my-4">
                  <mat-label class="font-bold">{{ 'BOTS.voicemailGreeting' | translate }}</mat-label>
                  <textarea matInput rows="2" formControlName="voicemailGreeting"></textarea>
                  <mat-hint>{{ 'BOTS.voicemailGreetingHint' | translate }}</mat-hint>
                </mat-form-field>
                <mat-form-field appearance="fill" class="w-full my-4">
                  <mat-label class="font-bold">{{ 'BOTS.voicemailMaxDurationSeconds' | translate }}</mat-label>
                  <input matInput type="number" min="30" max="600" formControlName="voicemailMaxDurationSeconds" />
                  <mat-hint>{{ 'BOTS.voicemailMaxDurationSecondsHint' | translate }}</mat-hint>
                  @if (
                    botSetupForm.get('step6.voicemailMaxDurationSeconds')?.hasError('min') &&
                    botSetupForm.get('step6.voicemailMaxDurationSeconds')?.touched
                  ) {
                    <mat-error>
                      {{ 'ERRORS.voicemailMaxDurationMin' | translate }}
                    </mat-error>
                  }
                  @if (
                    botSetupForm.get('step6.voicemailMaxDurationSeconds')?.hasError('max') &&
                    botSetupForm.get('step6.voicemailMaxDurationSeconds')?.touched
                  ) {
                    <mat-error>
                      {{ 'ERRORS.voicemailMaxDurationMax' | translate }}
                    </mat-error>
                  }
                </mat-form-field>
              </div>
            }
          </div>
          <div class="flex mt-4 justify-end">
            <div class="flex">
              <button class="px-8 mr-2 responsive-btn" mat-flat-button color="accent" (click)="prevStep()">
                <mat-icon>arrow_back</mat-icon>
                <span class="btn-text">{{ 'BUTTON_ACTIONS.back' | translate }}</span>
              </button>
              @if (isVoiceFollowUpStepVisible || isChatCustomizationStepVisible || isTeamSchedulingStepVisible) {
                <button
                  class="px-8 responsive-btn flex-row-reverse"
                  mat-flat-button
                  color="primary"
                  [disabled]="!isStepValid(5)"
                  (click)="goToNextFromTransferStep()"
                >
                  <span class="btn-text">{{ 'BUTTON_ACTIONS.next' | translate }}</span>
                  <mat-icon>arrow_forward</mat-icon>
                </button>
              }
            </div>
          </div>
        </mat-expansion-panel>

        <!-- Pronunciation Dictionary (voice bots only) -->
        @if (botSetupForm.get('step2.type')?.value === 'VOICE') {
          <mat-expansion-panel [expanded]="step() === 10" (opened)="setStep(10)">
            <mat-expansion-panel-header>
              <mat-panel-title>{{ 'BOTS.pronunciationTitle' | translate }}</mat-panel-title>
              <mat-icon class="mr-2">record_voice_over</mat-icon>
            </mat-expansion-panel-header>
            <div class="flex flex-col" [formGroup]="botSetupForm.get('step6')">
              <p class="text-secondary mt-2 mb-4">{{ 'BOTS.pronunciationDescription' | translate }}</p>

              @if (welcomeAudioStale) {
                <div
                  class="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10 p-3 mb-4"
                >
                  <mat-icon class="text-amber-600 dark:text-amber-400 shrink-0 icon-size-5">sync_problem</mat-icon>
                  <p class="text-sm text-amber-800 dark:text-amber-200 m-0">
                    {{ 'BOTS.welcomeSyncOutOfDate' | translate }}
                  </p>
                </div>
              }

              <div formArrayName="pronunciationOverrides" class="flex flex-col gap-3">
                @for (entry of pronunciationEntries.controls; track entryIndex; let entryIndex = $index) {
                  <div class="rounded-lg border border-border p-4" [formGroupName]="entryIndex">
                    <div class="flex flex-col gap-x-4 md:flex-row md:items-center">
                      <mat-form-field appearance="fill" class="flex-1">
                        <mat-label>{{ 'BOTS.pronunciationDisplay' | translate }}</mat-label>
                        <input matInput formControlName="display" placeholder="Critz" />
                        <mat-hint>{{ 'BOTS.pronunciationDisplayHint' | translate }}</mat-hint>
                      </mat-form-field>

                      <mat-icon class="hidden md:block mb-5 text-secondary shrink-0">arrow_forward</mat-icon>

                      <mat-form-field appearance="fill" class="flex-1">
                        <mat-label>{{ 'BOTS.pronunciationSpoken' | translate }}</mat-label>
                        <input matInput formControlName="spoken" placeholder="Crytes" />
                        <mat-hint>
                          @if (isMultiLanguagePronunciation) {
                            {{ 'BOTS.pronunciationSpokenAllLanguages' | translate }}
                          } @else {
                            {{ 'BOTS.pronunciationSpokenHint' | translate }}
                          }
                        </mat-hint>
                      </mat-form-field>

                      <button
                        class="mb-5 shrink-0"
                        mat-icon-button
                        type="button"
                        [disabled]="
                          !canPreviewPronunciation || !entry.get('spoken')?.value || previewingPronunciationIndex !== null
                        "
                        [matTooltip]="
                          (canPreviewPronunciation ? 'BOTS.pronunciationPreviewHint' : 'BOTS.pronunciationPreviewSaveFirst')
                            | translate
                        "
                        (click)="previewPronunciation(entryIndex)"
                      >
                        @if (previewingPronunciationIndex === entryIndex) {
                          <mat-spinner diameter="20"></mat-spinner>
                        } @else {
                          <mat-icon
                            [ngClass]="
                              playingPronunciationIndex === entryIndex
                                ? 'material-symbols-outlined animate-eq'
                                : 'material-symbols-outlined'
                            "
                            >{{ playingPronunciationIndex === entryIndex ? 'graphic_eq' : 'play_arrow' }}</mat-icon
                          >
                        }
                      </button>
                    </div>

                    @if (isMultiLanguagePronunciation) {
                      <div class="mt-1">
                        <button
                          type="button"
                          class="flex items-center gap-1 text-sm text-primary font-medium bg-transparent border-0 p-0 cursor-pointer"
                          (click)="togglePronunciationExpanded(entry)"
                        >
                          <mat-icon class="icon-size-5">{{
                            isPronunciationExpanded(entry) ? 'expand_more' : 'chevron_right'
                          }}</mat-icon>
                          <mat-icon class="icon-size-4">language</mat-icon>
                          <span>
                            {{ 'BOTS.pronunciationLanguageOverrides' | translate }} ({{
                              pronunciationEntryVariants(entryIndex).length
                            }})
                          </span>
                        </button>

                        @if (isPronunciationExpanded(entry)) {
                          <div
                            formArrayName="variants"
                            class="mt-2 ml-2 pl-3 flex flex-col gap-2 border-l-2 border-gray-200 dark:border-white/10"
                          >
                            @for (
                              variant of pronunciationEntryVariants(entryIndex).controls;
                              track variantIndex;
                              let variantIndex = $index
                            ) {
                              <div class="flex items-center gap-2" [formGroupName]="variantIndex">
                                <mat-form-field appearance="fill" class="w-44" subscriptSizing="dynamic">
                                  <mat-label>{{ 'BOTS.pronunciationVariantLanguage' | translate }}</mat-label>
                                  <mat-select formControlName="language">
                                    @for (lang of pronunciationOverrideLanguages; track lang.value) {
                                      <mat-option [value]="lang.value">{{ lang.label }}</mat-option>
                                    }
                                  </mat-select>
                                </mat-form-field>
                                <mat-form-field appearance="fill" class="flex-1" subscriptSizing="dynamic">
                                  <mat-label>{{ 'BOTS.pronunciationSpoken' | translate }}</mat-label>
                                  <input matInput formControlName="spoken" placeholder="Crytes" />
                                </mat-form-field>
                                <button
                                  class="shrink-0"
                                  mat-icon-button
                                  type="button"
                                  [disabled]="
                                    !canPreviewPronunciation ||
                                    !variant.get('spoken')?.value ||
                                    previewingPronunciationIndex !== null ||
                                    previewingVariantKey !== null
                                  "
                                  [matTooltip]="
                                    (canPreviewPronunciation
                                      ? 'BOTS.pronunciationPreviewHint'
                                      : 'BOTS.pronunciationPreviewSaveFirst'
                                    ) | translate
                                  "
                                  (click)="previewPronunciationVariant(entryIndex, variantIndex)"
                                >
                                  @if (isPreviewingVariant(entryIndex, variantIndex)) {
                                    <mat-spinner diameter="20"></mat-spinner>
                                  } @else {
                                    <mat-icon
                                      [ngClass]="
                                        isPlayingVariant(entryIndex, variantIndex)
                                          ? 'material-symbols-outlined animate-eq'
                                          : 'material-symbols-outlined'
                                      "
                                      >{{
                                        isPlayingVariant(entryIndex, variantIndex) ? 'graphic_eq' : 'play_arrow'
                                      }}</mat-icon
                                    >
                                  }
                                </button>
                                <button
                                  mat-icon-button
                                  type="button"
                                  [matTooltip]="'BOTS.pronunciationRemoveLanguage' | translate"
                                  (click)="removePronunciationVariant(entryIndex, variantIndex)"
                                >
                                  <mat-icon class="text-secondary">close</mat-icon>
                                </button>
                              </div>
                            }
                            <button
                              mat-button
                              type="button"
                              color="primary"
                              class="self-start"
                              (click)="addPronunciationVariant(entryIndex)"
                            >
                              <mat-icon>add</mat-icon>
                              <span>{{ 'BOTS.pronunciationAddLanguage' | translate }}</span>
                            </button>
                          </div>
                        }
                      </div>
                    }

                    <div class="flex items-center justify-between mt-3">
                      @switch (welcomeAudioStatus(entry.get('display')?.value)) {
                        @case ('baked') {
                          <span
                            class="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 dark:text-emerald-400"
                            [matTooltip]="'BOTS.welcomeAudioBakedHint' | translate"
                          >
                            <mat-icon class="icon-size-4">graphic_eq</mat-icon>
                            {{ 'BOTS.welcomeAudioBaked' | translate }}
                          </span>
                        }
                        @case ('pending') {
                          <span
                            class="inline-flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-400"
                            [matTooltip]="'BOTS.welcomeAudioPendingHint' | translate"
                          >
                            <mat-icon class="icon-size-4">schedule</mat-icon>
                            {{ 'BOTS.welcomeAudioPending' | translate }}
                          </span>
                        }
                        @default {
                          <span></span>
                        }
                      }
                      <button mat-button type="button" color="warn" (click)="removePronunciationEntry(entryIndex)">
                        <mat-icon>delete_outline</mat-icon>
                        <span>{{ 'BOTS.pronunciationRemove' | translate }}</span>
                      </button>
                    </div>
                  </div>
                } @empty {
                  <p class="text-sm text-secondary m-0">{{ 'BOTS.pronunciationEmpty' | translate }}</p>
                }
              </div>

              <button
                mat-flat-button
                color="primary"
                type="button"
                class="mt-4 self-start"
                (click)="addPronunciationEntry()"
              >
                <mat-icon>add</mat-icon>
                <span>{{ 'BOTS.pronunciationAdd' | translate }}</span>
              </button>

              <audio #pronunciationAudio id="pronunciationAudio"></audio>
            </div>
          </mat-expansion-panel>
        }

        <!-- SMS Follow-Up -->
        @if (isVoiceFollowUpStepVisible) {
          <mat-expansion-panel [expanded]="step() === 7" (opened)="setStep(7)" [disabled]="!isStepValid(5)">
            <mat-expansion-panel-header>
              <mat-panel-title>{{ 'BOTS.smsFollowUp' | translate }}</mat-panel-title>
              <mat-icon class="mr-2">sms</mat-icon>
            </mat-expansion-panel-header>
            <div class="flex flex-col" [formGroup]="botSetupForm.get('step7')">
              <div class="flex items-center justify-between py-2">
                <div class="flex flex-col">
                  <span class="font-bold">{{ 'BOTS.enableSmsFollowUp' | translate }}</span>
                  <span class="text-secondary text-sm">
                    {{ 'BOTS.enableSmsFollowUpDescription' | translate }}
                  </span>
                </div>
                <mat-slide-toggle
                  formControlName="enableSmsFollowUp"
                  [attr.aria-label]="'BOTS.enableSmsFollowUp' | translate"
                ></mat-slide-toggle>
              </div>
              @if (showSmsFollowUp) {
                <div class="flex flex-col mt-4">
                  <mat-label class="font-bold mb-2"
                    >{{ 'BOTS.linkedSmsBot' | translate }} <span class="text-red-500">*</span></mat-label
                  >
                  <mat-form-field appearance="fill" class="w-full">
                    <mat-select formControlName="linkedVoiceBot" [placeholder]="'BOTS.selectSmsBot' | translate">
                      @for (bot of smsBots; track bot._id) {
                        <mat-option [value]="bot._id">
                          {{ bot.displayName }}
                        </mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                  @if (smsBots.length === 0) {
                    <span class="text-red-500 text-sm -mt-4">{{ 'BOTS.noSmsBotsAvailable' | translate }}</span>
                  }
                </div>
              }
            </div>
            <div class="flex mt-4 justify-end">
              <div class="flex">
                <button class="px-8 mr-2 responsive-btn" mat-flat-button color="accent" (click)="prevStep()">
                  <mat-icon>arrow_back</mat-icon>
                  <span class="btn-text">{{ 'BUTTON_ACTIONS.back' | translate }}</span>
                </button>
                <button
                  class="px-8 responsive-btn flex-row-reverse"
                  mat-flat-button
                  color="primary"
                  (click)="goToNextFromSmsFollowUpStep()"
                >
                  <span class="btn-text">{{ 'BUTTON_ACTIONS.next' | translate }}</span>
                  <mat-icon>arrow_forward</mat-icon>
                </button>
              </div>
            </div>
          </mat-expansion-panel>
        }

        <!-- Team Scheduling -->
        @if (isTeamSchedulingStepVisible) {
          <mat-expansion-panel [expanded]="step() === 9" (opened)="setStep(9)" [disabled]="!isStepValid(5)">
            <mat-expansion-panel-header>
              <mat-panel-title>{{ 'BOTS.teamScheduling' | translate }}</mat-panel-title>
              <mat-icon class="mr-2">groups</mat-icon>
            </mat-expansion-panel-header>

            <div class="flex flex-col gap-4" [formGroup]="botSetupForm.get('step9')">
              <mat-form-field appearance="fill" class="w-full mt-4">
                <mat-label class="font-bold">{{ 'BOTS.fallbackTeamId' | translate }}</mat-label>
                <input matInput type="text" formControlName="fallbackTeamId" />
                <mat-hint>{{ 'BOTS.fallbackTeamIdHint' | translate }}</mat-hint>
                @if (
                  botSetupForm.get('step9.fallbackTeamId')?.hasError('maxlength') &&
                  botSetupForm.get('step9.fallbackTeamId')?.touched
                ) {
                  <mat-error>{{ 'ERRORS.fallbackTeamIdMaxLength' | translate }}</mat-error>
                }
              </mat-form-field>

              <div class="flex items-center justify-between py-2 border rounded-lg px-4">
                <div class="flex flex-col">
                  <span class="font-bold">{{ 'BOTS.forceDefaultTeamOnly' | translate }}</span>
                  <span class="text-secondary text-sm">
                    {{ 'BOTS.forceDefaultTeamOnlyDescription' | translate }}
                  </span>
                </div>
                <mat-slide-toggle formControlName="forceDefaultTeamOnly" color="primary">
                  <span class="sr-only">{{ 'BOTS.forceDefaultTeamOnly' | translate }}</span>
                </mat-slide-toggle>
              </div>
            </div>

            <div class="flex mt-4 justify-end">
              <div class="flex">
                <button
                  class="px-8 mr-2 responsive-btn"
                  mat-flat-button
                  color="accent"
                  (click)="goToPreviousFromTeamSchedulingStep()"
                >
                  <mat-icon>arrow_back</mat-icon>
                  <span class="btn-text">{{ 'BUTTON_ACTIONS.back' | translate }}</span>
                </button>
                @if (isChatCustomizationStepVisible) {
                  <button class="px-8 responsive-btn flex-row-reverse" mat-flat-button color="primary" (click)="setStep(8)">
                    <span class="btn-text">{{ 'BUTTON_ACTIONS.next' | translate }}</span>
                    <mat-icon>arrow_forward</mat-icon>
                  </button>
                }
              </div>
            </div>
          </mat-expansion-panel>
        }

        <!-- Chat Customization -->
        @if (isChatCustomizationStepVisible) {
          <mat-expansion-panel
            [expanded]="step() === 8"
            (opened)="onChatCustomizationStepOpened()"
            [disabled]="!isStepValid(5)"
          >
            <mat-expansion-panel-header>
              <mat-panel-title>{{ 'BOT_CUSTOMIZATION.chatbotCustomization' | translate }}</mat-panel-title>
              <mat-icon class="mr-2">palette</mat-icon>
            </mat-expansion-panel-header>

            <app-chatbot-customization [group]="$any(botSetupForm.get('step8'))"></app-chatbot-customization>

            <div class="flex mt-4 justify-end">
              <div class="flex">
                <button
                  class="px-8 mr-2 responsive-btn"
                  mat-flat-button
                  color="accent"
                  (click)="goToPreviousFromChatCustomizationStep()"
                >
                  <mat-icon>arrow_back</mat-icon>
                  <span class="btn-text">{{ 'BUTTON_ACTIONS.back' | translate }}</span>
                </button>
                <button
                  class="px-8 responsive-btn flex-row-reverse"
                  mat-flat-button
                  color="primary"
                  [disabled]="!isStepValid(8)"
                  (click)="nextStep()"
                >
                  <span class="btn-text">{{ 'BUTTON_ACTIONS.next' | translate }}</span>
                  <mat-icon>arrow_forward</mat-icon>
                </button>
              </div>
            </div>
          </mat-expansion-panel>
        }

        <!-- Allowed Domains -->
        @if (editMode && botSetupForm.get('step2.type')?.value === 'TEXT') {
          <mat-expansion-panel [expanded]="step() === 9" (opened)="setStep(9)" [disabled]="!isStepValid(5)">
            <mat-expansion-panel-header>
              <mat-panel-title>{{ 'BOTS.allowedDomainsConfiguration' | translate }}</mat-panel-title>
              <mat-icon class="mr-2">public</mat-icon>
            </mat-expansion-panel-header>

            <div class="flex flex-col">
              <div class="text-xl font-semibold">{{ 'BOTS.allowedDomains' | translate }}</div>
              <p class="text-secondary mt-2 mb-4">
                {{ 'BOTS.allowedDomainsDescription' | translate }}
              </p>

              @if (shouldShowEmptyAllowedDomainsWarning) {
                <div class="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 mb-4">
                  <mat-icon class="text-amber-700">warning_amber</mat-icon>
                  <span class="text-amber-800">{{ 'BOTS.emptyAllowedDomainsWarning' | translate }}</span>
                </div>
              }

              @if (isWildcardAllowedDomainEnabled) {
                <div class="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 mb-4">
                  <mat-icon class="text-amber-700">warning_amber</mat-icon>
                  <span class="text-amber-800">{{ 'BOTS.wildcardAllowedDomainsWarning' | translate }}</span>
                </div>
              }

              <div class="flex items-center justify-between py-2 mb-4">
                <div class="flex flex-col">
                  <span class="font-bold">{{ 'BOTS.allowAllOrigins' | translate }}</span>
                  <span class="text-secondary text-sm">{{ 'BOTS.allowAllOriginsHint' | translate }}</span>
                </div>
                <mat-slide-toggle
                  [checked]="isWildcardAllowedDomainEnabled"
                  [disabled]="isSavingAllowedDomains"
                  (change)="toggleWildcardAllowedDomain($event.checked)"
                  [attr.aria-label]="'BOTS.allowAllOriginsToggleAria' | translate"
                ></mat-slide-toggle>
              </div>

              <mat-label class="font-bold">{{ 'BOTS.domainUrl' | translate }}</mat-label>
              <div class="flex items-stretch gap-2 mt-2 allowed-domain-input-row">
                <mat-form-field appearance="fill" subscriptSizing="dynamic" class="w-full">
                  <input
                    matInput
                    [formControl]="allowedDomainControl"
                    placeholder="https://virtual-agent-prod.cdk-aiva.com"
                    autocomplete="off"
                    (input)="clearAllowedDomainValidationError()"
                    (keydown.enter)="$event.preventDefault(); addAllowedDomain()"
                  />
                  @if (allowedDomainValidationError) {
                    <mat-error>{{ allowedDomainValidationError | translate }}</mat-error>
                  }
                </mat-form-field>
                <button
                  class="px-5 responsive-btn identifier-item-add-btn"
                  mat-flat-button
                  color="primary"
                  type="button"
                  [disabled]="isSavingAllowedDomains"
                  (click)="addAllowedDomain()"
                >
                  <mat-icon>add</mat-icon>
                  {{ 'BUTTON_ACTIONS.add' | translate }}
                </button>
              </div>

              @if (nonWildcardAllowedDomains.length > 0) {
                <div class="flex flex-wrap gap-3 mt-2">
                  @for (domain of nonWildcardAllowedDomains; track domain) {
                    <div class="flex items-center bg-gray-100 rounded-[14px] px-2 mt-2">
                      <span>{{ domain }}</span>
                      <button
                        mat-icon-button
                        type="button"
                        class="ml-2 leading-none"
                        [disabled]="isSavingAllowedDomains"
                        (click)="removeAllowedDomain(domain)"
                        [attr.aria-label]="'Remove ' + domain"
                      >
                        <mat-icon class="icon-size-4">close</mat-icon>
                      </button>
                    </div>
                  }
                </div>
              }
            </div>
          </mat-expansion-panel>
        }

        <!-- Nora Configuration (visible only when NORA_FEATURES is enabled) -->
        @if (noraFeatures) {
          <mat-expansion-panel [expanded]="step() === 12" (opened)="setStep(12)">
            <mat-expansion-panel-header>
              <mat-panel-title>Nora Configuration</mat-panel-title>
              <mat-icon class="mr-2">tune</mat-icon>
            </mat-expansion-panel-header>
            <div class="flex flex-col" [formGroup]="botSetupForm.get('step3')">
              <mat-radio-group class="flex flex-col mb-4" formControlName="flow" aria-labelledby="nora-flow-label">
                <mat-label id="nora-flow-label" class="font-bold">Flow</mat-label>
                <mat-hint class="mb-2">Which flow the bot will follow.</mat-hint>
                <mat-radio-button value="AIVA1">AIVA1</mat-radio-button>
                <mat-radio-button value="AIVA2">AIVA2</mat-radio-button>
              </mat-radio-group>
              <mat-radio-group
                class="flex flex-col mb-4"
                formControlName="voicePipeline"
                aria-labelledby="nora-voice-pipeline-label"
              >
                <mat-label id="nora-voice-pipeline-label" class="font-bold">Voice Pipeline</mat-label>
                <mat-radio-button value="DEFAULT">Default flow (STT → LLM → TTS pipeline)</mat-radio-button>
                <mat-radio-button value="REALTIME"
                  >Realtime voice-to-voice — gpt-realtime-2.1 (speech-to-speech)</mat-radio-button
                >
                <!-- Flux TTS is English-only: offered only when the bot speaks English
                     alone. A bot already on FLUX keeps it selectable so the error
                     below is shown instead of the choice silently disappearing. -->
                <mat-radio-button
                  value="FLUX"
                  [disabled]="!fluxAvailable && botSetupForm.get('step3.voicePipeline')?.value !== 'FLUX'"
                  >Deepgram Flux (Flux STT → LLM → Flux TTS)</mat-radio-button
                >
                @if (!fluxAvailable && botSetupForm.get('step3.voicePipeline')?.value !== 'FLUX') {
                  <mat-hint class="mt-1">
                    Deepgram Flux voices are English-only, so Flux is available only when every language the bot speaks is
                    English.
                  </mat-hint>
                }
                @if (fluxEnglishOnlyError) {
                  <mat-error class="mt-2">
                    Deepgram Flux supports English only, and this bot also speaks another language. Choose another voice
                    pipeline, or make the bot English-only in Multilanguage Configuration, to save.
                  </mat-error>
                } @else if (botSetupForm.get('step3.voicePipeline')?.value === 'FLUX') {
                  <mat-hint class="mt-2">
                    STT: {{ fluxSttModel }}. TTS: {{ botSetupForm.get('step3.fluxVoice')?.value }}. The LLM is unchanged.
                  </mat-hint>
                } @else if (botSetupForm.get('step3.voicePipeline')?.value === 'REALTIME') {
                  <mat-hint class="mt-2">
                    Voice: {{ botSetupForm.get('step3.realtimeVoice')?.value }}. The bot's own voices are not used.
                  </mat-hint>
                }
              </mat-radio-group>
              @if (botSetupForm.get('step3.voicePipeline')?.value === 'FLUX') {
                <mat-radio-group
                  class="flex flex-col mb-4"
                  formControlName="fluxVoice"
                  aria-labelledby="nora-flux-voice-label"
                >
                  <mat-label id="nora-flux-voice-label" class="font-bold">Flux Voice</mat-label>
                  <mat-hint class="mb-2">The voice the bot speaks English with on live calls.</mat-hint>
                  @for (voice of fluxVoiceOptions; track voice.value) {
                    <div class="flex items-center">
                      <mat-radio-button [value]="voice.value">
                        <span class="font-medium">{{ voice.name }}</span>
                        <span class="text-secondary"> ({{ voice.value }}) · {{ voice.description }}</span>
                      </mat-radio-button>
                      <button
                        mat-icon-button
                        type="button"
                        class="ml-1"
                        [disabled]="voicePreviewLoading !== null && voicePreviewLoading !== voice.value"
                        [attr.aria-label]="
                          (voicePreviewPlaying === voice.value ? 'Stop preview of ' : 'Preview ') + voice.name
                        "
                        (click)="toggleFluxVoicePreview(voice.value, $event)"
                      >
                        @if (voicePreviewLoading === voice.value) {
                          <mat-spinner diameter="20"></mat-spinner>
                        } @else {
                          <mat-icon>{{ voicePreviewPlaying === voice.value ? 'stop_circle' : 'play_circle' }}</mat-icon>
                        }
                      </button>
                    </div>
                  }
                </mat-radio-group>
              }
              @if (botSetupForm.get('step3.voicePipeline')?.value === 'REALTIME') {
                <mat-radio-group
                  class="flex flex-col mb-4"
                  formControlName="realtimeVoice"
                  aria-labelledby="nora-realtime-voice-label"
                >
                  <mat-label id="nora-realtime-voice-label" class="font-bold">Realtime Voice</mat-label>
                  <mat-hint class="mb-2"
                    >The voice gpt-realtime speaks every line with on live calls, greeting included.</mat-hint
                  >
                  @for (voice of realtimeVoiceOptions; track voice.value) {
                    <div class="flex items-center">
                      <mat-radio-button [value]="voice.value">
                        <span class="font-medium">{{ voice.name }}</span>
                        <span class="text-secondary"> ({{ voice.value }}) · {{ voice.description }}</span>
                      </mat-radio-button>
                      <button
                        mat-icon-button
                        type="button"
                        class="ml-1"
                        [disabled]="voicePreviewLoading !== null && voicePreviewLoading !== voice.value"
                        [attr.aria-label]="
                          (voicePreviewPlaying === voice.value ? 'Stop preview of ' : 'Preview ') + voice.name
                        "
                        (click)="toggleRealtimeVoicePreview(voice.value, $event)"
                      >
                        @if (voicePreviewLoading === voice.value) {
                          <mat-spinner diameter="20"></mat-spinner>
                        } @else {
                          <mat-icon>{{ voicePreviewPlaying === voice.value ? 'stop_circle' : 'play_circle' }}</mat-icon>
                        }
                      </button>
                    </div>
                  }
                </mat-radio-group>
              }
              <!-- AIVA-2183: per-bot barge-in for the REALTIME and FLUX pipelines. -->
              @if (
                botSetupForm.get('step3.voicePipeline')?.value === 'REALTIME' ||
                botSetupForm.get('step3.voicePipeline')?.value === 'FLUX'
              ) {
                <div class="flex items-center justify-between py-4 border-t border-gray-200 dark:border-gray-700">
                  <div class="flex flex-col pr-4">
                    <span id="nora-caller-interrupt-label" class="font-bold">Caller can interrupt</span>
                    <span class="text-secondary text-sm">
                      On: the caller can cut the bot off mid-sentence. Off: the caller is not heard while the bot is talking.
                    </span>
                  </div>
                  <mat-slide-toggle
                    formControlName="callerCanInterrupt"
                    aria-labelledby="nora-caller-interrupt-label"
                  ></mat-slide-toggle>
                </div>
              }
            </div>
          </mat-expansion-panel>
        }

        <!-- AIVA-2091: Mileage Collection is an AIVA2 flow feature, so this section appears
             only once Flow is AIVA2 (Nora Configuration above). AIVA1 bots never ask. -->
        @if (isAiva2Bot) {
          <mat-expansion-panel [expanded]="step() === 14" (opened)="setStep(14)">
            <mat-expansion-panel-header>
              <mat-panel-title>{{ 'BOTS.mileageCollection' | translate }}</mat-panel-title>
              <mat-icon class="mr-2">speed</mat-icon>
            </mat-expansion-panel-header>
            <div class="flex flex-col" [formGroup]="botSetupForm.get('step3')">
              <mat-radio-group
                class="flex flex-col mb-4"
                formControlName="mileageCollection"
                [attr.aria-label]="'BOTS.mileageCollection' | translate"
              >
                <mat-hint class="mb-2">{{ 'BOTS.mileageCollectionDescription' | translate }}</mat-hint>
                @for (option of mileageCollectionOptions; track option.value) {
                  <mat-radio-button [value]="option.value">
                    <span class="font-medium">{{ option.labelKey | translate }}</span>
                    <span class="text-secondary"> · {{ option.hintKey | translate }}</span>
                  </mat-radio-button>
                }
              </mat-radio-group>
            </div>
          </mat-expansion-panel>
        }

        <div class="flex mt-6 justify-between items-center">
          @if (!editMode) {
            <button class="px-8 responsive-btn" mat-flat-button color="warn" (click)="confirmClearDraft()">
              <mat-icon>delete</mat-icon>
              <span class="btn-text">{{ 'BUTTON_ACTIONS.clearForm' | translate }}</span>
            </button>
          }
          <div class="flex ml-auto gap-2">
            <button
              class="px-8 responsive-btn flex-row-reverse"
              mat-flat-button
              color="primary"
              [disabled]="!canSubmitForm()"
              (click)="handleFinalSubmit()"
            >
              <span class="btn-text">
                {{ editMode ? ('BUTTON_ACTIONS.editBot' | translate) : ('BUTTON_ACTIONS.createBot' | translate) }}
              </span>
              <mat-icon>
                {{ editMode ? 'save' : 'add_circle' }}
              </mat-icon>
            </button>
          </div>
        </div>
      </mat-accordion>
    </form>
  </div>
</div>
